import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FilePdf, Ruler, CaretRight, FolderOpen, CloudArrowDown, CloudCheck, WifiSlash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { DesignerPlan } from "@/components/CountertopDesigner";
import { PinchZoom } from "@/components/PinchZoom";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const CACHE = "brioni-offline-v1";
const qKey = (id) => `offline_quote_${id}`;
const pdfKey = (id) => `/offline/quote-pdf/${id}`;

const cachedPdf = async (id) => {
  if (!("caches" in window)) return null;
  const c = await caches.open(CACHE);
  return c.match(pdfKey(id));
};

export const MobileQuoteDrawing = ({ quoteId, projectId }) => {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(false);
  const [savedAt, setSavedAt] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(qKey(quoteId)) || "null")?._saved_at || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    if (!quoteId) return;
    api
      .get(`/quotes/${quoteId}`)
      .then(({ data }) => setQ(data))
      .catch(() => {
        try {
          const c = JSON.parse(localStorage.getItem(qKey(quoteId)) || "null");
          if (c) {
            setQ(c);
            setOffline(true);
          }
        } catch {
          /* no cache */
        }
      });
  }, [quoteId]);

  const openPdf = async () => {
    setBusy(true);
    try {
      let blob;
      try {
        ({ data: blob } = await api.get(`/quotes/${quoteId}/pdf`, { responseType: "blob" }));
      } catch {
        const hit = await cachedPdf(quoteId);
        if (!hit) throw new Error("no cache");
        blob = await hit.blob();
        toast.message("Showing the offline copy");
      }
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Quote PDF isn't available offline yet — save it while you have signal");
    }
    setBusy(false);
  };

  const saveOffline = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/quotes/${quoteId}`);
      const stamp = new Date().toISOString();
      localStorage.setItem(qKey(quoteId), JSON.stringify({ ...data, _saved_at: stamp }));
      if ("caches" in window) {
        const { data: blob } = await api.get(`/quotes/${quoteId}/pdf`, { responseType: "blob" });
        const c = await caches.open(CACHE);
        await c.put(pdfKey(quoteId), new Response(blob, { headers: { "Content-Type": "application/pdf" } }));
      }
      setSavedAt(stamp);
      setQ(data);
      toast.success("Drawing and quote PDF saved on this phone");
    } catch {
      toast.error("Could not save for offline");
    }
    setBusy(false);
  };

  if (!quoteId && !projectId) return null;
  const areas = (q?.areas || []).filter((a) => (a.pieces || []).length > 0);
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4" data-testid="mobile-job-quote-drawing">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wide text-zinc-500">Quote &amp; drawing</div>
        {projectId && (
          <Link to={`/projects/${projectId}`} data-testid="mobile-job-open-project" className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-300">
            <FolderOpen size={13} weight="bold" /> Project <CaretRight size={11} weight="bold" />
          </Link>
        )}
      </div>
      {offline && (
        <div data-testid="mobile-job-offline-badge" className="mt-2 inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-200">
          <WifiSlash size={11} weight="bold" /> Offline copy
        </div>
      )}
      {q ? (
        <>
          <div className="mt-2 flex items-center justify-between text-sm">
            <span className="font-mono-data text-indigo-300">{q.code}</span>
            <span className="text-zinc-300">{q.status}</span>
            <span className="font-mono-data font-bold text-zinc-100">{money(q.totals?.total ?? q.total)}</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button type="button" data-testid="mobile-job-quote-pdf" disabled={busy} onClick={openPdf} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-200 disabled:opacity-50">
              <FilePdf size={15} weight="bold" /> Quote PDF
            </button>
            <Link to={`/quotes/${quoteId}?tab=drawing`} data-testid="mobile-job-open-drawing" className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-200">
              <Ruler size={15} weight="bold" /> Full drawing
            </Link>
          </div>
          <button
            type="button"
            data-testid="mobile-job-save-offline"
            disabled={busy || offline}
            onClick={saveOffline}
            className={`mt-2 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border text-[11px] font-semibold uppercase tracking-widest disabled:opacity-50 ${
              savedAt ? "border-emerald-500/40 text-emerald-200" : "border-zinc-700 text-zinc-300"
            }`}
          >
            {savedAt ? <CloudCheck size={15} weight="bold" /> : <CloudArrowDown size={15} weight="bold" />}
            {savedAt ? `Saved offline · ${new Date(savedAt).toLocaleDateString()} — tap to refresh` : "Save for offline"}
          </button>
          {areas.length > 0 ? (
            <div className="mt-3 space-y-3">
              {areas.map((a) => (
                <div key={a.id} data-testid={`mobile-job-drawing-${a.id}`}>
                  <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    {a.name} · {a.slab_color || q.slab_color || "—"}
                  </div>
                  <PinchZoom testid={`mobile-job-drawing-zoom-${a.id}`}>
                    <DesignerPlan pieces={a.pieces || []} light hideCutoutDims={q.hide_cutout_dims !== false} />
                  </PinchZoom>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-xs text-zinc-500">No drawing on this quote yet.</p>
          )}
        </>
      ) : (
        <p className="mt-2 text-xs text-zinc-500">{quoteId ? "Loading quote…" : "No quote linked to this job."}</p>
      )}
    </div>
  );
};
